import { useState } from "react";
import { router } from "expo-router";
import useClassActionsStore from "@/store/classActions";
import FeedServices from "@/services/feedServices";
import UnclaimedServices from "@/services/unclaimedServices";
import FBTrackingService from "@/services/fbTrackingService";
import { Platform } from "react-native";
import { storageUtils } from "@/utils";
import { APP_BASE_URL } from "@/constants/Configs";
import useUserStore from "@/store/user";
import { TRACKING_EVENTS } from "@/constants/trackingEvents";
import { showError } from "@/utils/toast";
import * as Sentry from "@sentry/react-native";

export function useHomeScreenActions(
  suitResponse: any,
  refetchFeed: () => void
) {
  const isWeb = Platform.OS === "web";
  const { setActiveSuit } = useClassActionsStore();
  const { userProfile, isFreePlan } = useUserStore();
  const [selectedUnclaimedItem, setSelectedUnclaimedItem] = useState<any>(null);
  const [selectedBankOfferItem, setSelectedBankOfferItem] = useState<any>(null);

  const handleHide = (id: string) => {
    FeedServices.hideItem(id).then((res) => {
      if (res.success) {
        refetchFeed();
      }
    });
  };

  const handleClaim = (data: any) => {
    if (data.type === "unclaimedMoney") {
      FBTrackingService.trackViewContent("unclaimedMoney");
      FBTrackingService.trackEvent(
        TRACKING_EVENTS.UNCLAIMED_MONEY_CLAIM_INITIATED,
        {
          eventData: {
            data: data,
          },
        }
      );
      setSelectedUnclaimedItem(data);
      return;
    }
    if (data.type === "bank") {
      setSelectedBankOfferItem(data);
      return;
    }
    FBTrackingService.trackViewContent("classAction");
    const userCredits = userProfile?.userCredits || 0;

    if (isWeb) {
      const sessionId = storageUtils.getSessionId();
      window.open(
        `${APP_BASE_URL}/file?select=${data.suitId}&sessionId=${sessionId}&fromWebFeed=true`
      );
      return;
    }
    if (userCredits <= 0 && isFreePlan) {
      router.push("/file/out-of-free-claims");
      return;
    }
    const suit = suitResponse?.data?.find(
      (suit: any) => suit.id === data.suitId
    );

    if (!suit) {
      Sentry.captureMessage("Suit lookup failed - suit not found in suitList", {
        level: "error",
        extra: {
          suitId: data.suitId,
          feedItemId: data.id,
          suitListLength: suitResponse?.data?.length || 0,
          hasSuitResponse: !!suitResponse,
        },
      });
      showError("This claim is temporarily unavailable. Please try again.");
      return;
    }

    setActiveSuit(suit);

    setTimeout(() => {
      router.push({
        pathname: "/file/sign",
      });
    }, 100);
  };

  const handleUnclaimedPopupClose = () => {
    setSelectedUnclaimedItem(null);
  };

  const handleBankOfferPopupClose = () => {
    setSelectedBankOfferItem(null);
  };

  const handleFileClaim = () => {
    if (selectedUnclaimedItem) {
      FBTrackingService.trackEvent(
        TRACKING_EVENTS.UNCLAIMED_MONEY_CLAIM_SUBMITTED,
        {
          eventData: {
            selectedUnclaimedItem: selectedUnclaimedItem,
          },
        }
      );

      UnclaimedServices.addClaimToSheet(selectedUnclaimedItem).then((res) => {
        if (res.success) {
          // Track success with result
          FBTrackingService.trackEvent(
            TRACKING_EVENTS.UNCLAIMED_MONEY_CLAIM_SUCCESS,
            {
              eventData: {
                selectedUnclaimedItem: selectedUnclaimedItem,
              },
            }
          );

          setSelectedUnclaimedItem(null);
          router.push({
            pathname: "/configuration",
            params: {
              title: `You just claimed $${
                selectedUnclaimedItem.amount || selectedUnclaimedItem.maxAmount
              } from ${selectedUnclaimedItem.businessName}!`,
              description: `If approved, your check will arrive in 4 to 8 weeks.`,
            },
          });
        } else {
          // Track failure with error
          FBTrackingService.trackEvent(
            TRACKING_EVENTS.UNCLAIMED_MONEY_CLAIM_FAILED,
            {
              eventData: {
                selectedUnclaimedItem: selectedUnclaimedItem,
              },
            }
          );
        }
      });
    }
  };

  const handleStartMonitoring = () => {
    FBTrackingService.trackViewContent("unclaimedMoney");
    FBTrackingService.trackEvent(
      TRACKING_EVENTS.UNCLAIMED_PROPERTY_MONITORING_STARTED,
      { eventData: {} }
    );
    router.push("/unclaimed/setup");
  };

  return {
    selectedUnclaimedItem,
    selectedBankOfferItem,
    handleHide,
    handleClaim,
    handleUnclaimedPopupClose,
    handleFileClaim,
    handleStartMonitoring,
    handleBankOfferPopupClose,
  };
}
